import type { EodBar } from "@/types/marketData";

/** Linearly-weighted moving average. Most recent bar gets weight = period. */
export function wma(values: number[], period: number): (number | null)[] {
  const out: (number | null)[] = [];
  const denom = (period * (period + 1)) / 2;
  for (let i = 0; i < values.length; i++) {
    if (i < period - 1) {
      out.push(null);
      continue;
    }
    let weighted = 0;
    for (let k = 0; k < period; k++) {
      weighted += values[i - period + 1 + k] * (k + 1);
    }
    out.push(weighted / denom);
  }
  return out;
}

export function sma(values: number[], period: number): (number | null)[] {
  const out: (number | null)[] = [];
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i];
    if (i >= period) sum -= values[i - period];
    out.push(i >= period - 1 ? sum / period : null);
  }
  return out;
}

export function ema(values: number[], period: number): (number | null)[] {
  const out: (number | null)[] = [];
  const k = 2 / (period + 1);
  let prev: number | null = null;
  let seed = 0;
  for (let i = 0; i < values.length; i++) {
    if (i < period - 1) {
      seed += values[i];
      out.push(null);
    } else if (i === period - 1) {
      seed += values[i];
      prev = seed / period;
      out.push(prev);
    } else {
      prev = values[i] * k + (prev as number) * (1 - k);
      out.push(prev);
    }
  }
  return out;
}

export function rsi(values: number[], period = 14): (number | null)[] {
  const out: (number | null)[] = new Array(values.length).fill(null);
  if (values.length < period + 1) return out;
  let gains = 0;
  let losses = 0;
  for (let i = 1; i <= period; i++) {
    const ch = values[i] - values[i - 1];
    if (ch >= 0) gains += ch;
    else losses -= ch;
  }
  let avgGain = gains / period;
  let avgLoss = losses / period;
  out[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  for (let i = period + 1; i < values.length; i++) {
    const ch = values[i] - values[i - 1];
    const gain = ch > 0 ? ch : 0;
    const loss = ch < 0 ? -ch : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    out[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return out;
}

export function bollingerWidth(values: number[], period = 20, mult = 2): (number | null)[] {
  const out: (number | null)[] = [];
  for (let i = 0; i < values.length; i++) {
    if (i < period - 1) {
      out.push(null);
      continue;
    }
    const slice = values.slice(i - period + 1, i + 1);
    const mean = slice.reduce((a, b) => a + b, 0) / period;
    const variance = slice.reduce((a, b) => a + (b - mean) ** 2, 0) / period;
    const sd = Math.sqrt(variance);
    out.push((2 * mult * sd) / mean); // normalized width
  }
  return out;
}

export function returnPct(bars: EodBar[], lookback: number): number | null {
  if (bars.length <= lookback) return null;
  const last = bars[bars.length - 1].adjusted_close;
  const past = bars[bars.length - 1 - lookback].adjusted_close;
  return ((last - past) / past) * 100;
}

export function maxClose(bars: EodBar[], lookback: number): number {
  return Math.max(...bars.slice(-lookback).map((b) => b.adjusted_close));
}

export function minClose(bars: EodBar[], lookback: number): number {
  return Math.min(...bars.slice(-lookback).map((b) => b.adjusted_close));
}

/**
 * Convert a unix timestamp (ms) into the US-Eastern trading-day date string
 * (YYYY-MM-DD). Providers tag daily bars by the *trading* day,
 * which is Eastern-time, not UTC — without this, after-hours ticks roll over
 * past UTC midnight and create phantom bars for "tomorrow".
 */
export function tradingDayET(unixMs: number): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(unixMs));
}

export function fromDaysAgo(days: number): string {
  // Pad for weekends/holidays so EOD has enough bars.
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000 * 1.6).toISOString().slice(0, 10);
}

/** Add calendar days to a YYYY-MM-DD string (UTC date arithmetic). */
export function shiftDateDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
